/** Locale-correct group separators for the share card's hero numeral — "5,000" en-US,
 *  «5 000» ru-RU (non-breaking space per Intl). `tag` is the interface locale's
 *  regional tag from the language registry (ADR-111 D1), `useLocale().tag`. */
export function formatMilestoneNumeral(n: number, tag: string): string {
  return n.toLocaleString(tag)
}

// Milestone thresholds — the only place these numbers live. Lower a threshold here to
// smoke-test the milestone completion screen on-device with a realistic session size.
export const MILESTONE_THRESHOLDS = {
  streakDays: [10, 50, 100, 365] as const,
  vocabularyWords: [100, 500, 1000, 2500, 5000, 10000, 20000] as const,
}

export type Milestone =
  | { type: 'vocabulary'; value: number }
  | { type: 'streak'; value: number }

/**
 * Detects at most one milestone for a just-completed session.
 * Vocabulary milestones use threshold-CROSSING (pre < threshold <= post) so a session
 * that jumps past a round number in one go still fires. Streak milestones use equality
 * since a streak can only grow by exactly 1 day per session. Vocabulary takes priority
 * over streak when both fire the same session — exactly one milestone, never a carousel.
 */
/**
 * Reads the next configured threshold after the one just crossed, plus the remaining
 * distance to it. Returns null when `value` is the final configured threshold for that
 * milestone type — the completion screen omits the "next milestone" line in that case.
 */
export function getNextMilestone(
  milestone: Milestone,
  currentValue: number,
): { threshold: number; remaining: number } | null {
  const thresholds = milestone.type === 'vocabulary'
    ? MILESTONE_THRESHOLDS.vocabularyWords
    : MILESTONE_THRESHOLDS.streakDays
  const next = (thresholds as readonly number[]).find((t) => t > milestone.value)
  if (next === undefined) return null
  return { threshold: next, remaining: Math.max(next - currentValue, 0) }
}

export function detectMilestone(
  preWordsKnown: number | null,
  postWordsKnown: number | null,
  streakDays: number | null | undefined,
): Milestone | null {
  if (preWordsKnown != null && postWordsKnown != null) {
    const crossed = MILESTONE_THRESHOLDS.vocabularyWords.filter(
      (t) => preWordsKnown < t && postWordsKnown >= t,
    )
    if (crossed.length > 0) {
      return { type: 'vocabulary', value: Math.max(...crossed) }
    }
  }
  if (streakDays != null && (MILESTONE_THRESHOLDS.streakDays as readonly number[]).includes(streakDays)) {
    return { type: 'streak', value: streakDays }
  }
  return null
}
